import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoShopifyOrderCancelReason } from "@redotech/redo-fulfillment-group-common/order-cancel-reason";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import {
  GENERIC_CANCEL_ORDER_ERROR_MESSAGE,
  OUTSTANDING_FULFILLMENTS_CANCEL_ORDER_ERROR_MESSAGE,
} from "@redotech/redo-merchant-fulfillment-rpc/schema/cancel-fulfillment-group";
import { Provider } from "@redotech/redo-order-common/provider";
import { toast } from "@redotech/redo-web/alert";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../../outbound-labels-rpc-client";

export interface CancelOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  groups: readonly FulfillmentGroupData[];
  orderNames?: string[];
}

const cancelReasonOptions: RedoListItem<RedoShopifyOrderCancelReason>[] = [
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Customer,
    value: RedoShopifyOrderCancelReason.Customer,
    text: "Customer - The customer wanted to cancel the order",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Declined,
    value: RedoShopifyOrderCancelReason.Declined,
    text: "Declined - Payment was declined",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Fraud,
    value: RedoShopifyOrderCancelReason.Fraud,
    text: "Fraud - The order was fraudulent",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Inventory,
    value: RedoShopifyOrderCancelReason.Inventory,
    text: "Inventory - There was insufficient inventory",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Staff,
    value: RedoShopifyOrderCancelReason.Staff,
    text: "Staff - Staff made an error",
  },
  {
    type: "text",
    id: RedoShopifyOrderCancelReason.Other,
    value: RedoShopifyOrderCancelReason.Other,
    text: "Other - Order was canceled for an unlisted reason",
  },
];

function getCancelOrderErrorMessage(error: unknown): string {
  if (
    error instanceof Error &&
    error.message === OUTSTANDING_FULFILLMENTS_CANCEL_ORDER_ERROR_MESSAGE
  ) {
    return error.message;
  }

  return GENERIC_CANCEL_ORDER_ERROR_MESSAGE;
}

export const CancelOrderModal = memo(function CancelOrderModal({
  isOpen,
  onClose,
  onConfirm,
  orderNames = [],
  groups,
}: CancelOrderModalProps) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const [reason, setReason] = useState<RedoShopifyOrderCancelReason>(
    RedoShopifyOrderCancelReason.Other,
  );
  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(false);
  const [refund, setRefund] = useState<boolean>(true);
  const [restock, setRestock] = useState<boolean>(true);

  const groupIds = groups.map((g) => g._id);

  // Only show Shopify-specific options if all groups are from Shopify
  const isShopifyOnly = useMemo(() => {
    return (
      groups.length > 0 &&
      groups.every((group) =>
        group.providers?.some((provider) => provider === Provider.SHOPIFY),
      )
    );
  }, [groups]);

  const orderText =
    orderNames.length > 0
      ? orderNames.length === 1
        ? `order ${orderNames[0]}`
        : `${orderNames.length} orders`
      : "this order";

  const selectedReasonOption = useMemo(
    () => cancelReasonOptions.find((option) => option.value === reason),
    [reason],
  );

  const [cancelOrderLoad, cancelOrder] = useTriggerLoad(async () => {
    try {
      await client.cancelFulfillmentGroups({
        fulfillmentGroupIds: groupIds,
        reason,
        notifyCustomer,
        refund,
        restock,
      });

      onConfirm();
      onClose();
    } catch (error) {
      toast(getCancelOrderErrorMessage(error), { variant: "error" });
    }
  });

  const modalProps: RedoModalProps = {
    TitleIcon: XCircleIcon,
    theme: "warn",
    title: "Cancel order",
    modalSize: "sm",
    isOpen,
    onModalCloseRequested: onClose,
    primaryButton: {
      text: "Cancel order",
      onClick: () => cancelOrder(),
      pending: cancelOrderLoad.pending,
    },
    secondaryButton: {
      text: "Keep Order",
      onClick: onClose,
      disabled: cancelOrderLoad.pending,
    },
  };

  return (
    <RedoModal {...modalProps}>
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" textColor="secondary">
          Are you sure you want to cancel {orderText}? This may refund and
          notify the customer and close all shipments associated with the order.
        </Text>

        <RedoSingleSelectDropdownInput<RedoShopifyOrderCancelReason>
          label="Cancel Reason"
          options={cancelReasonOptions}
          optionSelected={({ item }) => setReason(item.value)}
          selectedItem={selectedReasonOption}
        />

        {isShopifyOnly && (
          <Flex dir="column" gap="md">
            <RedoCheckbox
              description="Send cancellation notification to customer"
              label="Notify Customer"
              setValue={(value) => setNotifyCustomer(!!value)}
              size="sm"
              value={notifyCustomer}
            />

            <RedoCheckbox
              description="Process refund for this order"
              label="Refund"
              setValue={(value) => setRefund(!!value)}
              size="sm"
              value={refund}
            />

            <RedoCheckbox
              description="Return items to inventory"
              label="Restock"
              setValue={(value) => setRestock(!!value)}
              size="sm"
              value={restock}
            />
          </Flex>
        )}
      </Flex>
    </RedoModal>
  );
});
